import React from "react"
import styled from "styled-components"
import Flex from "@irati/ui/src/Flex"
import {Card, Hr} from "@irati/ui/src/Layout"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {H2, H3} from "@irati/ui/src/Typography"
import FormattedMessage, {FormattedCurrency} from "@irati/utils/lang"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import Loading from "@irati/ui/src/Loading"
import {useBudgetsPermissions, useBudgetsList} from "@irati/wallet/hooks/budget"
import BudgetBar from "../../Budgeting/BudgetBar"
import {useFlexiPointsWalletExpired} from "../../../hooks/expiry"
import FormattedErrorText from "../../styled/FormattedErrorText"
import Categories from "./Categories"
import {BudgetProps} from "./shared"
import Expirary from "./Expiry"
import {LearnMore} from "./LearnMore"
import {WalletRoutes} from "@irati-web/wallet/src/enums/Routes"
import useAuth from "@irati/auth/helpers/auth/useAuth"

const StyledLink = styled(ButtonRouterLink)`
  width: fit-content;
`

export default function AdminBudget(props: BudgetProps) {
  const {canManageCategories} = useBudgetsPermissions()
  const {load, data} = useBudgetsList()
  const {expiry, error: expiryError} = useFlexiPointsWalletExpired(props.wallet.uuid)
  const auth = useAuth()
  const canTopUpRewardPoints = auth.productPermissions?.wallet?.flexiRewards?.canTopUpRewardPoints

  const showLearnMore = canManageCategories && !data?.hasBudgets
  const showManageBudget = data?.hasBudgets && !showLearnMore

  const {
    wallet: {availableBalanceInCents, currency}
  } = props

  return (
    <Card marginTop={2}>
      <Flex direction="column" gap={3}>
        <Flex wrap="wrap" gap={3}>
          <H3>
            <FormattedMessage id="wallet.reward.intro.walletBalance" />
            {currency && (
              <H2 as="span">
                <FormattedCurrency value={availableBalanceInCents ?? 0} currency={currency} />
              </H2>
            )}
          </H3>

          <Flex gap={2} justifyContent="flex-end" alignItems="flex-start" flex="1 0 fit-content">
            {canTopUpRewardPoints && (
              <ButtonRouterLink
                id="rewardWalletTopUpButton"
                to="/flexi-checkout/top-up"
                onClick={() =>
                  tracking.buyPoints({
                    source: "rp_reward_wallet_header"
                  })
                }>
                <FormattedMessage id="wallet.reward.budget.rewardWallet.cta.buyPoints" />
              </ButtonRouterLink>
            )}
            <ButtonRouterLink
              id="rewardWalletDistributeRewardsButton"
              to="/flexi-assign/recipients"
              onClick={() => tracking.giveRewardPoints("rp_reward_wallet_header")}>
              <FormattedMessage id="wallet.reward.budget.rewardWallet.cta.givePoints" />
            </ButtonRouterLink>
          </Flex>
        </Flex>

        {expiryError && <FormattedErrorText error={expiryError} />}
        {expiry.hasExpiry && <Expirary date={expiry.date} amount={expiry.amount} onClick={props.onClick} />}

        {!data && <Loading load={load} />}
        {data && (
          <>
            {showLearnMore && <LearnMore />}
            <BudgetBar />
            <Hr flush />
            <Categories height={155} />
            {showManageBudget && (
              <StyledLink
                id="rewardWalletViewBudgetButton"
                to={WalletRoutes.FLEXI_BUDGET}
                small
                onClick={() => {
                  tracking.viewBudgets("rp_reward_wallet_header")
                }}>
                <FormattedMessage id="wallet.reward.budget.rewardWallet.cta.viewBudget" />
              </StyledLink>
            )}
          </>
        )}
      </Flex>
    </Card>
  )
}
